import { z } from 'zod';

/**
 * The Zemax orchestrator's in-memory task queue, proxied by the backend for
 * the /admin/debug-queue page. Mirrors `TaskQueue.stats()`, `TaskRecord` and
 * `PendingTask` in zemax-analysis-service/utils/task_queue.py — both services
 * ship from this repo, so the shapes move together.
 */

export const ZemaxQueueStatsSchema = z.object({
  num_workers: z.number(),
  max_queue_size: z.number(),
  current_queue_size: z.number(),
  is_running: z.boolean(),
  default_timeout: z.number(),
  tasks_submitted: z.number(),
  tasks_completed: z.number(),
  tasks_failed: z.number(),
  tasks_timed_out: z.number(),
  queue_full_rejections: z.number(),
});
export type ZemaxQueueStats = z.infer<typeof ZemaxQueueStatsSchema>;

/**
 * One queue task. `history` entries are `TaskRecord`s and carry every field;
 * `pending` entries are `PendingTask`s, which have not finished and so omit
 * the completion fields — hence their optionality here.
 */
export const ZemaxQueueTaskSchema = z.object({
  id: z.string(),
  operation_name: z.string(),
  /** 'pending' | 'executing' while queued; 'completed' | 'failed' | 'timed_out' | 'cancelled' once retired. */
  status: z.string(),
  queued_at: z.string(),
  started_at: z.string().nullable(),
  queue_wait_ms: z.number(),
  /** Which orchestrator worker coroutine ran it; null until one picks it up. */
  worker_id: z.number().nullable(),
  user_email: z.string().nullable(),
  conversation_id: z.string().nullable(),
  engine: z.string().nullable(),
  completed_at: z.string().nullable().optional(),
  /** Null when the task was retired without ever starting. */
  execution_ms: z.number().nullable().optional(),
  total_ms: z.number().optional(),
  error: z.string().nullable().optional(),
  stage_timings: z.record(z.string(), z.number()).nullable().optional(),
});
export type ZemaxQueueTask = z.infer<typeof ZemaxQueueTaskSchema>;

export const ZemaxQueueSchema = z.object({
  stats: ZemaxQueueStatsSchema,
  /** Most recent first, bounded by the orchestrator's retention deque. */
  history: z.array(ZemaxQueueTaskSchema),
  pending: z.array(ZemaxQueueTaskSchema),
});
export type ZemaxQueue = z.infer<typeof ZemaxQueueSchema>;
